import { cva } from 'class-variance-authority';

/**
 * Base selectable card styles applied to all card variants.
 */
export const baseSelectableCardStyles = [
  'bg-neutral-white enabled:hover:bg-neutral-gray-000',
  'border-neutral-gray-400 enabled:hover:border-neutral-black',
  'border',
  'cursor-pointer',
  'disabled:cursor-not-allowed',
  'disabled:bg-neutral-gray-100',
  'disabled:border-neutral-gray-300',
  'flex items-center gap-4',
  'h-full',
  'p-4',
  'relative',
  'rounded-lg',
  'transition-all duration-200',
  'w-full',
].join(' ');

/**
 * SelectableCard variants using class-variance-authority.
 * Provides success, warning, and error states with appropriate styling.
 */
export const selectableCardVariants = cva(baseSelectableCardStyles, {
  variants: {
    variant: {
      // Default variant with neutral styling
      default: [
        'data-[selected=true]:bg-neutral-gray-100',
        'data-[selected=true]:border-neutral-gray-800',
      ],

      // Success variant - Green theme
      success: [
        'data-[selected=true]:bg-accent-surface-green',
        'data-[selected=true]:border-accent-green-500',
      ],

      // Warning variant - Yellow/Orange theme
      warning: [
        'data-[selected=true]:bg-accent-surface-yellow',
        'data-[selected=true]:border-accent-orange-300',
      ],

      // Error variant - Red theme
      error: [
        'data-[selected=true]:bg-accent-surface-red',
        'data-[selected=true]:border-accent-red-500',
      ],
    },
  },

  // Default variant
  defaultVariants: {
    variant: 'default',
  },
});

/**
 * Icon container variants for different states and variants.
 */
export const cardIconContainerVariants = cva(
  [
    'bg-neutral-gray-100 data-[selected=true]:bg-neutral-white',
    'border-2 border-neutral-gray-300 data-[selected=true]:border-neutral-white',
    'flex flex-shrink-0 items-center justify-center',
    'h-11 w-11 rounded-full px-0.5 py-0.5',
    'pointer-events-none',
  ],
  {
    variants: {
      variant: {
        default: [],
        success: [],
        warning: [],
        error: [],
      },
    },

    defaultVariants: {
      variant: 'default',
    },
  },
);

/**
 * Icon variants for different states and variants.
 */
export const cardIconVariants = cva('', {
  variants: {
    variant: {
      // Default icon color for any unspecified variant
      default: 'text-neutral-gray-700',

      // Success variant icon color
      success: 'text-accent-green-500',

      // Warning variant icon color
      warning: 'text-accent-orange-300',

      // Error variant icon color
      error: 'text-accent-red-500',
    },
  },

  defaultVariants: {
    variant: 'default',
  },
});

/**
 * Check icon variants for different states and variants.
 */
export const checkIconVariants = cva('', {
  variants: {
    variant: {
      // Unselected state has no check icon color
      unselected: 'text-neutral-gray-400',

      // Default check icon color for any unspecified variant
      default: 'text-neutral-gray-700',

      // Success variant check icon color
      success: 'text-accent-green-500',

      // Warning variant check icon color
      warning: 'text-accent-orange-300',

      // Error variant check icon color
      error: 'text-accent-red-500',
    },
  },

  defaultVariants: {
    variant: 'unselected',
  },
});
